import {ZyusetuFieldKeyFragmentFragment, ZyusetuFieldValueAnalyzedPayloadFragmentFragment} from "@/graphql/graphql";
import {
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {MyDialogTitle} from "@/components/dialog/MyDialogTitle.tsx";
import {useCallback, useMemo, useState} from "react";
import {LoadingButton} from "@/components/input/LoadingButton.tsx";

interface ZyusetuDocumentAiTableViewerDialogProps {
  payload: ZyusetuFieldValueAnalyzedPayloadFragmentFragment;
  open: boolean;
  onClose: () => void;
  onGenerateItems?: (selectedContents: string) => Promise<void>;
  fieldKey?: ZyusetuFieldKeyFragmentFragment; // 対象のフィールドキー情報
}

interface SelectedRowContent {
  idx: number;
  contents: {
    keyName: string;
    content: string;
  }[];
}

const keyNameToLabel: Record<string, string> = {
  "purpose": "目的",
  "rank": "順位番号",
  "registration_date": "登録日",
  "registration_number": "登録番号",
  "rights_other_address": "権利者の住所",
  "rights_other_name": "権利者の名前",
  "rights_other": "その他の権利"
}

const keyNameIndex: Record<string, number> = {
  rank: 0,
  registration_date: 1,
  purpose: 2,
  rights_other_address: 3,
  rights_other_name: 4,
  registration_number: 5,
  rights_other: 6
}

export function ZyusetuDocumentAiTableViewerDialog(
  {
    payload,
    open,
    onClose,
    onGenerateItems,
    fieldKey
  }: ZyusetuDocumentAiTableViewerDialogProps
) {
  // 選択された行のデータを管理するstate
  const [selectedRows, setSelectedRows] = useState<SelectedRowContent[]>([]);
  
  const sortByIndex = useCallback((a: string, b: string) => {
    const aIdx = keyNameIndex[a]
    const bIdx = keyNameIndex[b]
    
    if (aIdx === undefined && bIdx === undefined) {
      return 0
    }
    if (aIdx > bIdx) {
      return 1
    }
    if (aIdx < bIdx) {
      return -1
    }
    return 0
  }, [])
  
  const getHeads = useMemo((): string[] => {
    const heads: string[] = Array.from(new Set(payload.properties.map((value: {
      keyName: string
    }) => value.keyName)))
    return heads.sort(
      (a, b) => {
        return sortByIndex(a, b)
      }
    ).map((head) => {
      return keyNameToLabel[head]
    })
  }, [payload.properties, sortByIndex])
  
  const getContents: Map<number, {
    keyName: string,
    content: string
  }[]> = useMemo(() => {
    return payload.properties.reduce((acc: Map<number, {
      keyName: string,
      content: string
    }[]>, value: {
      keyName: string,
      idx: number,
      content: string
    }) => {
      if (acc.has(value.idx)) {
        acc.get(value.idx)?.push({
          keyName: value.keyName,
          content: value.content
        })
      } else {
        acc.set(value.idx, [{
          keyName: value.keyName,
          content: value.content
        }])
      }
      return acc
    }, new Map<number, {
      keyName: string,
      content: string
    }[]>())
  }, [payload.properties])
  
  // チェックボックスの状態変更ハンドラ
  const handleRowSelect = (idx: number, contents: { keyName: string; content: string }[]) => {
    setSelectedRows(prev => {
      const isSelected = prev.some(row => row.idx === idx);
      if (isSelected) {
        return prev.filter(row => row.idx !== idx);
      } else {
        return [...prev, {
          idx,
          contents: contents.sort((a, b) => sortByIndex(a.keyName, b.keyName))
        }];
      }
    });
  };
  
  const isRowSelected = (idx: number) => selectedRows.some(row => row.idx === idx);
  
  const handleGenerateItems = async () => {
    await onGenerateItems?.(JSON.stringify(selectedRows));
    onClose();
  };
  
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  // テーブルコンテナの高さを計算
  const tableContainerHeight = 'calc(100vh - 180px)';
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          maxHeight: '100vh',
          height: '100vh',
          m: 0
        }
      }}
    >
      <MyDialogTitle onClose={onClose}>
        コンテンツをAI生成
      </MyDialogTitle>
      <DialogContent sx={{p: 2, position: 'relative'}}>
        {fieldKey && (
          <Box sx={{mb: 2}}>
            <Typography variant="h6" component="div" sx={{display: 'flex', alignItems: 'center', mb: 1}}>
              対象フィールド：
              <Chip
                label={fieldKey.label}
                color="primary"
                sx={{
                  ml: 1,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  height: 'auto',
                  py: 0.5
                }}
              />
            </Typography>
            <Divider/>
          </Box>
        )}
        <TableContainer
          component={Paper}
          sx={{
            height: tableContainerHeight,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.05)',
            }
          }}
        >
          <Table stickyHeader size={isSmallScreen ? "small" : "medium"} sx={{tableLayout: 'fixed'}}>
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  style={{width: '48px'}}
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 3
                  }}
                />
                {getHeads.map((head) => {
                  return (
                    <TableCell
                      key={`head-${head}`}
                      sx={{
                        height: "40px",
                        width: head === "権利者の住所" || head === "権利者の名前" || head === "その他の権利" ? '300px' : '150px',
                        fontWeight: 'bold',
                        backgroundColor: theme.palette.background.paper,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {head}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {
                Array.from(getContents.entries(
                )).map(([key, contents]) => (
                  <TableRow
                    key={`valueArray-${key}`}
                    sx={{
                      height: 'auto',
                      minHeight: '30px',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      },
                      transition: 'background-color 0.2s'
                    }}
                    selected={isRowSelected(key)}
                    onClick={() => handleRowSelect(key, contents)}
                  >
                    <TableCell
                      padding="checkbox"
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 2
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isRowSelected(key)}
                        onChange={() => handleRowSelect(key, contents)}
                      />
                    </TableCell>
                    {contents.sort((a, b) => sortByIndex(
                      a.keyName, b.keyName
                    )).map(
                      content => (
                        <TableCell
                          key={`content-${content.keyName}-${content.content}`}
                          sx={{
                            height: "auto",
                            minHeight: "40px",
                            padding: '8px 16px',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            verticalAlign: 'top'
                          }}
                        >
                          {content.content}
                        </TableCell>
                      ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{p: 2, justifyContent: 'flex-end'}}>
        <LoadingButton
          variant="contained"
          disabled={selectedRows.length === 0}
          onClick={handleGenerateItems}
          disableFullWidth
          props={{
            sx: {
              minWidth: '240px',
              fontWeight: 'bold'
            }
          }}
        >
          選択した項目をもとに事項を生成する
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
